import React, { useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { AuthService } from "services/auth.service";
import { IKeyAndPassword } from "models/church.types";
import logoCiel from "assets/img/Logo-ciel.jpg";

export const ResetPasswordFinish: React.FC = () => {
  const history = useHistory();
  const location = useLocation();

  // Extraction du paramètre 'key' depuis la query string (React Router v5)
  const queryParams = new URLSearchParams(location.search);
  const key = queryParams.get("key") || "";

  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    if (!key) {
      setError("Clé de réinitialisation manquante ou invalide dans l'URL.");
      return;
    }

    if (newPassword.length < 4 || newPassword.length > 100) {
      setError("Le mot de passe doit contenir entre 4 et 100 caractères.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }

    setLoading(true);

    const payload: IKeyAndPassword = {
      key,
      newPassword,
    };

    try {
      await AuthService.finishPasswordReset(payload);
      setSuccess(true);
      setTimeout(() => {
        history.push("/auth/login");
      }, 4000);
    } catch (err: any) {
      console.error("Erreur lors de la validation du nouveau mot de passe :", err);
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "La clé est invalide ou a expiré. Veuillez refaire une demande.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 h-full">
      <div className="flex content-center items-center justify-center h-full">
        <div className="w-full lg:w-4/12 px-4">
          <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-[#0F172A] border-0">
            {/* En-tête avec Logo */}
            <div className="text-white text-center mt-10 font-bold px-6">
              <div className="w-20 h-20 bg-white text-[#253a5c] rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl border-4 border-slate-700">
                <img
                  src={logoCiel}
                  alt="Logo Église"
                  className="w-12 h-12 object-contain"
                />
              </div>
              <h3 className="text-2xl tracking-wide uppercase">
                Nouveau Mot de Passe
              </h3>
              <p className="text-slate-400 text-xs font-normal mt-2">
                Saisissez votre nouveau mot de passe sécurisé
              </p>
            </div>

            <div className="flex-auto px-4 lg:px-10 py-10">
              {/* Alertes d'erreur ou de succès */}
              {error && (
                <div className="mb-4 text-xs bg-red-500/20 border border-red-500 text-red-300 p-3 rounded font-medium">
                  <i className="fas fa-exclamation-circle mr-2"></i>
                  {error}
                </div>
              )}
              {success && (
                <div className="mb-4 text-xs bg-emerald-500/20 border border-emerald-500 text-emerald-300 p-3 rounded font-medium">
                  <i className="fas fa-check-circle mr-2"></i>
                  Mot de passe réinitialisé avec succès ! Redirection vers la connexion...
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Champ Nouveau Mot de Passe */}
                <div className="relative w-full mb-6">
                  <div className="relative flex w-full flex-wrap items-stretch">
                    <span className="z-10 h-full leading-snug font-normal absolute text-center text-slate-400 bg-transparent rounded text-base items-center justify-center w-10 pl-3 py-4">
                      <i className="fas fa-lock"></i>
                    </span>
                    <input
                      name="newPassword"
                      type="password"
                      className="border-0 px-3 py-4 pl-12 placeholder-slate-400 text-slate-600 bg-white rounded text-sm shadow focus:outline-none focus:ring-2 focus:ring-[#D4AF37] w-full ease-linear transition-all duration-150"
                      placeholder="Nouveau mot de passe"
                      required
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={loading || success}
                    />
                  </div>
                </div>

                {/* Champ Confirmation Mot de Passe */}
                <div className="relative w-full mb-6">
                  <div className="relative flex w-full flex-wrap items-stretch">
                    <span className="z-10 h-full leading-snug font-normal absolute text-center text-slate-400 bg-transparent rounded text-base items-center justify-center w-10 pl-3 py-4">
                      <i className="fas fa-check-double"></i>
                    </span>
                    <input
                      name="confirmPassword"
                      type="password"
                      className="border-0 px-3 py-4 pl-12 placeholder-slate-400 text-slate-600 bg-white rounded text-sm shadow focus:outline-none focus:ring-2 focus:ring-[#D4AF37] w-full ease-linear transition-all duration-150"
                      placeholder="Confirmez le mot de passe"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={loading || success}
                    />
                  </div>
                </div>

                {/* Bouton de validation */}
                <div className="text-center">
                  <button
                    className="bg-[#D4AF37] text-[#1e293b] active:bg-yellow-600 text-sm font-bold uppercase px-6 py-4 rounded shadow-lg hover:shadow-xl outline-none focus:outline-none w-full ease-linear transition-all duration-150 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    type="submit"
                    disabled={loading || success}
                  >
                    {loading ? (
                      <>
                        <i className="fas fa-circle-notch fa-spin mr-2"></i> Enregistrement...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save mr-2"></i> Valider le mot de passe
                      </>
                    )}
                  </button>
                </div>

                {/* Retour à la connexion */}
                <div className="text-center mt-6">
                  <Link
                    to="/auth/login"
                    className="text-slate-400 hover:text-white transition-all text-xs flex items-center justify-center gap-2"
                  >
                    <i className="fas fa-arrow-left text-xs"></i>
                    <span>Retour à la connexion</span>
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordFinish;